import { useEffect, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';
import { getSuppliers } from '../../services/SupplierService';
import { getUnlinkedReceivedInvoiceItems } from '../../services/ElectronicBillingService';
import SnackbarUtils from '../../utils/SnackbarUtils';

// Pick a supplier and see every line from its recent invoices that is not linked yet.
// "Vincular" hands those lines to the regular link dialog as a synthetic invoice.
const SupplierUnlinkedItemsDialog = ({ open, onClose, onLink }) => {
  const [suppliers, setSuppliers] = useState([]);
  const [supplier, setSupplier] = useState(null);
  const [items, setItems] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setSupplier(null);
    setItems(null);
    getSuppliers(1, 10000, '', { skipGlobalErrorRedirect: true })
      .then((response) => {
        const list = Array.isArray(response) ? response : response?.providers || response?.data || [];
        setSuppliers(list.filter((entry) => entry?.rut));
      })
      .catch(() => SnackbarUtils.error('No se pudieron cargar los proveedores.'));
  }, [open]);

  const selectSupplier = async (value) => {
    setSupplier(value);
    setItems(null);
    if (!value) return;
    setLoading(true);
    try {
      setItems(await getUnlinkedReceivedInvoiceItems(value.rut));
    } catch (error) {
      console.error('Error loading unlinked items', error);
      SnackbarUtils.error('No se pudieron cargar los ítems del proveedor.');
    } finally {
      setLoading(false);
    }
  };

  const handleLink = () => {
    onLink({
      synthetic: true,
      rucEmisor: supplier.rut,
      items: items.map((item) => ({
        externalItemId: item.external_item_id,
        description: item.description,
        quantity: item.last_quantity,
        unitPrice: item.last_unit_price,
      })),
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Vincular ítems por proveedor</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Mostramos los ítems de las facturas de los últimos 6 meses que todavía no están vinculados a un insumo o producto.
        </Typography>
        <Autocomplete
          options={suppliers}
          value={supplier}
          getOptionLabel={(option) => option?.name || ''}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          onChange={(_, value) => selectSupplier(value)}
          renderInput={(params) => <TextField {...params} label="Proveedor" size="small" />}
        />
        <Box sx={{ mt: 2 }}>
          {loading && <CircularProgress size={24} />}
          {!loading && items && items.length === 0 && (
            <Alert severity="success">Este proveedor no tiene ítems sin vincular en los últimos 6 meses.</Alert>
          )}
          {!loading && items && items.length > 0 && (
            <Alert severity="info">
              Hay {items.length} {items.length === 1 ? 'ítem sin vincular' : 'ítems sin vincular'}.
            </Alert>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cerrar</Button>
        <Button variant="contained" onClick={handleLink} disabled={loading || !items?.length}>Vincular</Button>
      </DialogActions>
    </Dialog>
  );
};

export default SupplierUnlinkedItemsDialog;
